import React, { useState } from "react";
import styles from "../../Application.module.scss";
import reviewStyles from "../../../create-app/components/application-form/components/review-submit/style.module.scss";
import ButtonIcon from "../../../../components/ButtonIcon/ButtonIcon";
import useCommonStore from "../../../../stores/common";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import CaretRight24 from "../../../../assets/svg/icons/common/CaretRight24";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const BUSINESS_ACTIVITY_OPTIONS = [
  { label: "Travel Agent", value: "travel_agent" },
  { label: "Tour Operator", value: "tour_operator" },
  { label: "Event Ticket Sellers (Non-Sporting)", value: "event_ticket_sellers_non_sporting" },
  { label: "Stand Alone Travel Ticket Sellers & Tour Operators", value: "stand_alone_travel_ticket_sellers_tour_operators" },
  { label: "Event Organisers (Non-Sporting)", value: "event_organisers_non_sporting" },
  { label: "Event Organisers (Sporting)", value: "event_organisers_sporting" },
  { label: "Third Party Event/Ticket Sellers/Touting", value: "third_party_event_ticket_sellers_touting" },
  { label: "Secondary Ticket Market (“C2C) / “Ticket Exchanges", value: "secondary_ticket_market_c2c_ticket_exchanges" },
  { label: "Other", value: "other" },
];

const SEASONALITY_OPTIONS = [
  { key: "pct_annual_sales", label: "% of total annual sales processed in this month" },
  { key: "max_monthly_card_sales", label: "Estimated maximum monthly card sales (USD)" },
  { key: "min_monthly_card_sales", label: "Estimated minimum monthly card sales (USD)" },
  { key: "monthly_total_sales", label: "Estimated monthly total sales (card and non-card) for the company in this month (USD)" },
];

const DELIVERY_PERIODS = [
  { key: "1_7_days", label: "1-7 days" },
  { key: "8_14_days", label: "8-14 days" },
  { key: "15_30_days", label: "15-30 days" },
  { key: "31_60_days", label: "31-60 days" },
  { key: "61_90_days", label: "61-90 days" },
  { key: "91_120_days", label: "91-120 days" },
  { key: "121_150_days", label: "121-150 days" },
  { key: "151_180_days", label: "151-180 days" },
  { key: "over_180_days", label: "Over 180 days" },
];

const SectionHeader = ({ title }) => (
  <div className={reviewStyles.sectionHeader}>
    <span className={reviewStyles.sectionTitle}>{title}</span>
    <div className={reviewStyles.sectionLine} />
  </div>
);

function TravelAddendum({ travelAddendumData }) {
  const expandAppDetails = useCommonStore((state) => state.expandDetails);
  const [expanded, setExpanded] = useState(false);
  const isMobile = useMediaQuery(768);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const data = travelAddendumData?.data || {};

  const getSeasonalityCellValue = (option, month) => {
    const fieldName = `travel_seasonality_${option.key}_${month.toLowerCase()}`;
    let value = data[fieldName];
    if (value === undefined && option.key === "monthly_total_sales") {
      value = data[`travel_seasonality_${month.toLowerCase()}`];
    }
    return value != null && value !== "" ? `${value}` : "-";
  };

  const depositPct = data.travel_average_deposit_percentage ?? data.travel_average_deposit;
  const depositToTravel = data.travel_average_time_deposit_travel ?? data.travel_months_deposit_balance;
  const balanceToTravel = data.travel_average_time_balance_travel ?? data.travel_months_balance_delivery;
  const subtotal = (
    (parseFloat(data.travel_sales_third_party_packages) || 0) +
    (parseFloat(data.travel_agency_accommodation_only) || 0) +
    (parseFloat(data.travel_agency_non_flight_transport) || 0) +
    (parseFloat(data.travel_flight_own_merchant) || 0) +
    (parseFloat(data.travel_flight_airline_merchant) || 0) +
    (parseFloat(data.travel_other_sales_agent) || 0)
  );

  return (
    <div
      className={`${isMobile ? styles.mobile_business_info : styles.business_information} ${expandAppDetails ? styles.expanded : ""}`}
    >
      <div className={isMobile ? styles.business_info_header : styles.head} onClick={toggleExpanded}>
        {isMobile ? (
          <>
            <CaretRight24 className={`${styles.caret_icon} ${expanded ? styles.expanded : ""}`} />
            <h4>Travel Addendum</h4>
          </>
        ) : (
          <>
            <ButtonIcon>
              <CaretRight24 className={expanded ? styles.expanded : ""} />
            </ButtonIcon>
            <h5>Travel Addendum</h5>
          </>
        )}
      </div>

      <div
        className={`${isMobile ? styles.mobile_content : styles.body} ${expanded ? (isMobile ? styles.expanded : styles.body_expanded) : ""} ${expandAppDetails ? styles.app_details_expanded : ""}`}
      >
        {expanded && (
          <div className={reviewStyles.reviewContent}>
            {/* Business Activities */}
            <div className={reviewStyles.section}>
              <SectionHeader title="Business Activities" />
              <div className={reviewStyles.detailsGrid}>
                <div className={reviewStyles.detailItem}>
                  <div className={reviewStyles.detailLabel}>Business Activities</div>
                  <div className={reviewStyles.detailValue}>
                    {data.travel_business_activities?.length > 0
                      ? data.travel_business_activities
                          .map((val) => BUSINESS_ACTIVITY_OPTIONS.find((opt) => opt.value === val)?.label ?? val)
                          .join(", ")
                      : "-"}
                  </div>
                </div>
                {data.travel_business_activities?.includes("other") && (
                  <div className={reviewStyles.detailItem}>
                    <div className={reviewStyles.detailLabel}>Other - please provide further details</div>
                    <div className={reviewStyles.detailValue}>{data.travel_other_business_activity || "-"}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Gross Annual Turnover */}
            <div className={reviewStyles.section}>
              <SectionHeader title="Gross Annual Turnover" />
              <div className={reviewStyles.detailsGrid}>
                <div className={reviewStyles.detailItem}>
                  <div className={reviewStyles.detailLabel}>Sales of third party packages</div>
                  <div className={reviewStyles.detailValue}>
                    {data.travel_sales_third_party_packages != null && data.travel_sales_third_party_packages !== ""
                      ? `${data.travel_sales_third_party_packages}%`
                      : "-"}
                  </div>
                </div>
                <div className={reviewStyles.detailItem}>
                  <div className={reviewStyles.detailLabel}>Agency sale of accommodation only</div>
                  <div className={reviewStyles.detailValue}>
                    {data.travel_agency_accommodation_only != null && data.travel_agency_accommodation_only !== ""
                      ? `${data.travel_agency_accommodation_only}%`
                      : "-"}
                  </div>
                </div>
                <div className={reviewStyles.detailItem}>
                  <div className={reviewStyles.detailLabel}>Agency sale of non-flight transport only</div>
                  <div className={reviewStyles.detailValue}>
                    {data.travel_agency_non_flight_transport != null && data.travel_agency_non_flight_transport !== ""
                      ? `${data.travel_agency_non_flight_transport}%`
                      : "-"}
                  </div>
                </div>
                <div className={reviewStyles.detailItem}>
                  <div className={reviewStyles.detailLabel}>Flight only - own merchant facility</div>
                  <div className={reviewStyles.detailValue}>
                    {data.travel_flight_own_merchant != null && data.travel_flight_own_merchant !== ""
                      ? `${data.travel_flight_own_merchant}%`
                      : "-"}
                  </div>
                </div>
                <div className={reviewStyles.detailItem}>
                  <div className={reviewStyles.detailLabel}>Flight only - airline merchant facility</div>
                  <div className={reviewStyles.detailValue}>
                    {data.travel_flight_airline_merchant != null && data.travel_flight_airline_merchant !== ""
                      ? `${data.travel_flight_airline_merchant}%`
                      : "-"}
                  </div>
                </div>
                <div className={reviewStyles.detailItem}>
                  <div className={reviewStyles.detailLabel}>Other sales as agent</div>
                  <div className={reviewStyles.detailValue}>
                    {data.travel_other_sales_agent != null && data.travel_other_sales_agent !== ""
                      ? `${data.travel_other_sales_agent}%`
                      : "-"}
                  </div>
                </div>
                {data.travel_other_sales_agent && data.travel_other_sales_agent !== "0" && (
                  <div className={reviewStyles.detailItem}>
                    <div className={reviewStyles.detailLabel}>Describe &apos;Other sales&apos; as appropriate</div>
                    <div className={reviewStyles.detailValue}>{data.travel_other_sales_description || "-"}</div>
                  </div>
                )}
                 <div className={reviewStyles.detailItem}>
                  <div className={reviewStyles.detailLabel}>SUBTOTAL</div>
                  <div className={reviewStyles.detailValue}>{subtotal || "-"}</div>
                </div>
                <div className={reviewStyles.detailItem}>
                  <div className={reviewStyles.detailLabel}>Total Gross Turnover</div>
                  <div className={reviewStyles.detailValue}>{data.travel_total_gross_turnover || "-"}</div>
                </div>
              </div>
            </div>

            {/* Airlines / Travel Suppliers */}
            {data.travel_airline_suppliers?.length > 0 && (
              <div className={reviewStyles.section}>
                <SectionHeader title="Airlines / Travel Suppliers" />
                <div className={reviewStyles.supplierGrid5Col}>
                  <div className={reviewStyles.supplierGridHeader}>Name</div>
                  <div className={reviewStyles.supplierGridHeader}>Country of base</div>
                  <div className={reviewStyles.supplierGridHeader}>Payment terms</div>
                  <div className={reviewStyles.supplierGridHeader}>% share of all flight spend</div>
                  <div className={reviewStyles.supplierGridHeader}>Average % of package cost</div>
                  {data.travel_airline_suppliers.map((supplier, index) => (
                    <React.Fragment key={index}>
                      <div className={reviewStyles.supplierGridCell}>{supplier.name || "-"}</div>
                      <div className={reviewStyles.supplierGridCell}>{supplier.country || "-"}</div>
                      <div className={reviewStyles.supplierGridCell}>{supplier.payment_terms || "-"}</div>
                      <div className={reviewStyles.supplierGridCell}>
                        {supplier.flight_spend_share != null && supplier.flight_spend_share !== ""
                          ? `${supplier.flight_spend_share}%`
                          : "-"}
                      </div>
                      <div className={reviewStyles.supplierGridCell}>
                        {supplier.average_package_cost != null && supplier.average_package_cost !== ""
                          ? `${supplier.average_package_cost}%`
                          : "-"}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
                <div className={reviewStyles.detailItem}>
                  <div className={reviewStyles.detailLabel}>Total % spend on Travel</div>
                  <div className={reviewStyles.detailValue}>
                    {data.travel_total_spend_travel != null && data.travel_total_spend_travel !== ""
                      ? `${data.travel_total_spend_travel}%`
                      : "-"}
                  </div>
                </div>
              </div>
            )}

            {/* Hotels / Accommodation Suppliers */}
            {data.travel_hotel_suppliers?.length > 0 && (
              <div className={reviewStyles.section}>
                <SectionHeader title="Hotels / Accommodation Suppliers" />
                <div className={reviewStyles.supplierGrid5Col}>
                  <div className={reviewStyles.supplierGridHeader}>Name</div>
                  <div className={reviewStyles.supplierGridHeader}>Country of base</div>
                  <div className={reviewStyles.supplierGridHeader}>Payment terms</div>
                  <div className={reviewStyles.supplierGridHeader}>% share of all hotel spend</div>
                  <div className={reviewStyles.supplierGridHeader}>Average % of package cost</div>
                  {data.travel_hotel_suppliers.map((supplier, index) => (
                    <React.Fragment key={index}>
                      <div className={reviewStyles.supplierGridCell}>{supplier.name || "-"}</div>
                      <div className={reviewStyles.supplierGridCell}>{supplier.country || "-"}</div>
                      <div className={reviewStyles.supplierGridCell}>{supplier.payment_terms || "-"}</div>
                      <div className={reviewStyles.supplierGridCell}>
                        {supplier.hotel_spend_share != null && supplier.hotel_spend_share !== ""
                          ? `${supplier.hotel_spend_share}%`
                          : "-"}
                      </div>
                      <div className={reviewStyles.supplierGridCell}>
                        {supplier.average_package_cost != null && supplier.average_package_cost !== ""
                          ? `${supplier.average_package_cost}%`
                          : "-"}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
                <div className={reviewStyles.detailItem}>
                  <div className={reviewStyles.detailLabel}>Total % spend on Accommodation</div>
                  <div className={reviewStyles.detailValue}>
                    {data.travel_total_spend_accommodation != null && data.travel_total_spend_accommodation !== ""
                      ? `${data.travel_total_spend_accommodation}%`
                      : "-"}
                  </div>
                </div>
              </div>
            )}

            {/* Local Services */}
            {data.travel_local_services?.length > 0 && (
              <div className={reviewStyles.section}>
                <SectionHeader title="Local Services" />
                <div className={reviewStyles.supplierGrid5Col}>
                  <div className={reviewStyles.supplierGridHeader}>Service Provider Name</div>
                  <div className={reviewStyles.supplierGridHeader}>Country of base</div>
                  <div className={reviewStyles.supplierGridHeader}>Payment terms</div>
                  <div className={reviewStyles.supplierGridHeader}>% share of &apos;other&apos; service provider spend</div>
                  <div className={reviewStyles.supplierGridHeader}>Avg % of package cost</div>
                  {data.travel_local_services.map((service, index) => (
                    <React.Fragment key={index}>
                      <div className={reviewStyles.supplierGridCell}>{service.name || "-"}</div>
                      <div className={reviewStyles.supplierGridCell}>{service.country || "-"}</div>
                      <div className={reviewStyles.supplierGridCell}>{service.payment_terms || "-"}</div>
                      <div className={reviewStyles.supplierGridCell}>
                        {service.service_spend_share != null && service.service_spend_share !== ""
                          ? `${service.service_spend_share}%`
                          : "-"}
                      </div>
                      <div className={reviewStyles.supplierGridCell}>
                        {service.average_package_cost != null && service.average_package_cost !== ""
                          ? `${service.average_package_cost}%`
                          : "-"}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
                <div className={reviewStyles.detailItem}>
                  <div className={reviewStyles.detailLabel}>Total % spend on Other elements</div>
                  <div className={reviewStyles.detailValue}>
                    {data.travel_total_spend_other != null && data.travel_total_spend_other !== ""
                      ? `${data.travel_total_spend_other}%`
                      : "-"}
                  </div>
                </div>
              </div>
            )}

            {/* Countries of Travel */}
            {data.travel_countries?.length > 0 && (
              <div className={reviewStyles.section}>
                <SectionHeader title="Countries of Travel" />
                <div className={reviewStyles.countriesGrid2Col}>
                  <div className={reviewStyles.countriesGridHeader}>Country Name</div>
                  <div className={reviewStyles.countriesGridHeader}>% of overall trade</div>
                  {data.travel_countries.map((country, index) => (
                    <React.Fragment key={index}>
                      <div className={reviewStyles.countriesGridCell}>{country.name || "-"}</div>
                      <div className={reviewStyles.countriesGridCell}>
                        {country.trade_share != null && country.trade_share !== "" ? `${country.trade_share}%` : "-"}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}

            {/* Seasonality */}
            <div className={reviewStyles.section}>
              <SectionHeader title="Seasonality" />
              <div className={reviewStyles.seasonalityReviewGrid}>
                <div className={reviewStyles.seasonalityReviewHeader} />
                {SEASONALITY_OPTIONS.map((option) => (
                  <div key={option.key} className={reviewStyles.seasonalityReviewColumnHeader}>
                    {option.label}
                  </div>
                ))}
                {MONTHS.map((month) => (
                  <React.Fragment key={month}>
                    <div className={reviewStyles.seasonalityReviewRowHeader}>{month}</div>
                    {SEASONALITY_OPTIONS.map((option) => {
                      const fieldName = `travel_seasonality_${option.key}_${month.toLowerCase()}`;
                      return (
                        <div key={fieldName} className={reviewStyles.seasonalityReviewCell}>
                          {getSeasonalityCellValue(option, month)}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Customer Payment Terms + delivery periods (same section as review) */}
            <div className={reviewStyles.section}>
              <SectionHeader title="Customer Payment Terms" />
              <div className={reviewStyles.detailsGrid}>
                <div className={reviewStyles.detailItem}>
                  <div className={reviewStyles.detailLabel}>Average deposit paid (%)</div>
                  <div className={reviewStyles.detailValue}>
                    {depositPct != null && depositPct !== "" ? `${depositPct}%` : "-"}
                  </div>
                </div>
                <div className={reviewStyles.detailItem}>
                  <div className={reviewStyles.detailLabel}>Average time between deposit payment and travel (months)</div>
                  <div className={reviewStyles.detailValue}>
                    {depositToTravel != null && depositToTravel !== "" ? `${depositToTravel}` : "-"}
                  </div>
                </div>
                <div className={reviewStyles.detailItem}>
                  <div className={reviewStyles.detailLabel}>Average time between balance payment and travel (months)</div>
                  <div className={reviewStyles.detailValue}>
                    {balanceToTravel != null && balanceToTravel !== "" ? `${balanceToTravel}` : "-"}
                  </div>
                </div>
              </div>
              <p className={reviewStyles.deliverySubtitle}>PERIOD FROM DATE OF CARD TRANSACTION TO DATE OF TRAVEL/EVENT/STAY</p>
              <div className={reviewStyles.detailsGrid}>
                {DELIVERY_PERIODS.map((period) => {
                  const fieldName = `travel_delivery_${period.key}`;
                  const value = data[fieldName];
                  return (
                    <div className={reviewStyles.detailItem} key={period.key}>
                      <div className={reviewStyles.detailLabel}>{period.label} (%)</div>
                      <div className={reviewStyles.detailValue}>
                        {value != null && value !== "" ? `${value}%` : "-"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Additional Information */}
            <div className={reviewStyles.section}>
              <SectionHeader title="Additional Information" />
              <div className={reviewStyles.detailsGrid}>
                <div className={reviewStyles.detailItem}>
                  <div className={reviewStyles.detailLabel}>Consumer protection</div>
                  <div className={reviewStyles.detailValue}>{data.travel_consumer_protection || "-"}</div>
                </div>
                {data.travel_website_address ? (
                  <div className={reviewStyles.detailItem}>
                    <div className={reviewStyles.detailLabel}>Website Address</div>
                    <div className={reviewStyles.detailValue}>{data.travel_website_address}</div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TravelAddendum;
